import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTotalOrderStatistics } from '@/hooks/useOrder';
import ReactECharts from 'echarts-for-react';
import {
  getTotalOrderPieOption,
  numberFormat,
  currencyFormat,
} from './ChartUtils';
import { ShoppingCart, TrendingUp, DollarSign } from 'lucide-react';
import LoadingCircle from '@/components/LoadingCircle';

interface TotalOrderChartProps {
  selectedYear: number;
  onYearChange: (year: number) => void;
}

const TotalOrderChart = ({
  selectedYear,
  onYearChange,
}: TotalOrderChartProps) => {
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  const {
    data,
    isLoading: loading,
    error,
  } = useTotalOrderStatistics(selectedYear);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Tổng Đơn Hàng Theo Năm</h2>
              <p className="text-green-100 text-xs">
                Phân tích doanh thu và hiệu suất
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64">
            <LoadingCircle />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Tổng Đơn Hàng Theo Năm</h2>
              <p className="text-green-100 text-xs">
                Phân tích doanh thu và hiệu suất
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64 text-luxury-600">
            {error.message || 'Không thể tải dữ liệu'}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Tổng Đơn Hàng Theo Năm</h2>
              <p className="text-green-100 text-xs">
                Phân tích doanh thu và hiệu suất
              </p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-center h-64 text-luxury-600">
            Không có dữ liệu
          </div>
        </div>
      </div>
    );
  }

  const totalOrders = data.totalOrders;
  const totalAmount = data.totalAmount;
  const averageAmount = data.averageAmount;

  return (
    <div className="bg-gradient-to-br from-white to-luxury-50/30 rounded-xl shadow-lg border border-luxury-200/50 overflow-hidden w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Tổng Đơn Hàng Theo Năm</h2>
              <p className="text-green-100 text-xs">
                Phân tích doanh thu và hiệu suất
              </p>
            </div>
          </div>
          <div className="min-w-[100px]">
            <Select
              value={selectedYear.toString()}
              onValueChange={(value) => onYearChange(parseInt(value))}
            >
              <SelectTrigger className="w-full luxury-input bg-white/20 backdrop-blur-sm border-white/30 hover:border-white/50 transition-all duration-200 h-9 text-white">
                <SelectValue placeholder="Năm" />
              </SelectTrigger>
              <SelectContent className="border-luxury-200 bg-white/95 backdrop-blur-sm">
                {years.map((year) => (
                  <SelectItem
                    key={year}
                    value={year.toString()}
                    className="hover:bg-luxury-50"
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-3 sm:p-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 flex flex-col items-center border border-blue-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-blue-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Tổng đơn hàng
          </span>
          <span className="text-2xl font-bold text-blue-700 group-hover:text-blue-800 transition-colors">
            {numberFormat(totalOrders)}
          </span>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 flex flex-col items-center border border-green-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-green-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Tổng doanh thu
          </span>
          <span className="text-lg font-bold text-green-600 group-hover:text-green-700 transition-colors">
            {currencyFormat(totalAmount)}
          </span>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 flex flex-col items-center border border-purple-200/50 shadow-md hover:shadow-lg transition-all duration-300 group">
          <div className="p-2 bg-purple-500/10 rounded-full mb-2 group-hover:scale-110 transition-transform duration-200">
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-gray-600 text-xs font-medium mb-1">
            Trung bình/đơn
          </span>
          <span className="text-lg font-bold text-purple-700 group-hover:text-purple-800 transition-colors">
            {currencyFormat(averageAmount)}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="p-3 sm:p-4">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-luxury-200/50">
          <h3 className="text-lg font-semibold text-luxury-900 mb-4">
            Phân Bố Đơn Hàng Theo Tháng
          </h3>
          <div className="h-[400px]">
            <ReactECharts
              option={getTotalOrderPieOption(data.monthlyData)}
              style={{ height: 400 }}
              notMerge={true}
              lazyUpdate={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalOrderChart;
